import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
  Image,
  LayoutDashboard,
  ListChecks,
  LogOut,
  type LucideIcon,
  Menu,
  PenSquare,
  Plug,
  Settings,
  Shield,
  Sparkles,
  Type,
  Users,
  X,
} from 'lucide-react';
import {
  Avatar,
  Badge,
  Button,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownSeparator,
  Tooltip,
} from '../ui';
import { NotificationBell } from './NotificationBell';
import { ThemeToggle } from './ThemeToggle';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { useSessionStore } from '../../stores/session';
import { cn } from '../../lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

/** Mirrors the route table in App.tsx. */
const PRIMARY_NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/composer', label: 'Composer', icon: PenSquare },
  { to: '/posts', label: 'Posts', icon: ListChecks },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/media', label: 'Media', icon: Image },
];

const WORKSPACE_NAV: NavItem[] = [
  { to: '/connections', label: 'Connections', icon: Plug },
  { to: '/brand', label: 'Brand', icon: Type },
  { to: '/style', label: 'Writing style', icon: Sparkles },
  { to: '/team', label: 'Team', icon: Users },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const COLLAPSE_KEY = 'pap-sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/* Sidebar                                                             */
/* ------------------------------------------------------------------ */

function NavRow({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group relative flex w-full items-center gap-3 rounded-md py-2 text-sm transition-colors',
          collapsed ? 'justify-center px-0' : 'px-3',
          isActive
            ? 'bg-elevated font-medium text-fg'
            : 'text-muted hover:bg-elevated/60 hover:text-fg',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              'absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-accent transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
            aria-hidden
          />
          <item.icon className="size-4 shrink-0" aria-hidden />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip content={item.label} side="right" delayMs={120} wrapperClassName="w-full">
      {link}
    </Tooltip>
  ) : (
    link
  );
}

function NavSection({
  items,
  title,
  collapsed,
  onNavigate,
}: {
  items: NavItem[];
  title?: string;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  return (
    <div className="space-y-0.5">
      {title && !collapsed && (
        <p className="px-3 pb-1 pt-5 text-[11px] font-medium uppercase tracking-wide text-subtle">
          {title}
        </p>
      )}
      {title && collapsed && <div className="my-3 h-px bg-border" aria-hidden />}
      {items.map((item) => (
        <NavRow key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

function Wordmark({ collapsed }: { collapsed: boolean }) {
  return (
    <div className={cn('flex items-center gap-2.5 px-1 py-1', collapsed && 'justify-center px-0')}>
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-fg"
        aria-hidden
      >
        <Sparkles className="size-4" />
      </span>
      {!collapsed && (
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold tracking-tight text-fg">
            Post Across
          </span>
          <span className="block truncate text-[11px] text-subtle">Platforms</span>
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shell                                                               */
/* ------------------------------------------------------------------ */

/** Sidebar + topbar frame for every signed-in page. */
export function AppShell() {
  const user = useSessionStore((state) => state.user);
  const aiProvider = useSessionStore((state) => state.aiProvider);
  const simulatePublish = useSessionStore((state) => state.simulatePublish);
  const devMode = useSessionStore((state) => state.devMode);
  const signOut = useSessionStore((state) => state.signOut);

  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* storage disabled — the preference is simply not remembered */
    }
  }, [collapsed]);

  // Route changes always close the mobile drawer.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    navigate('/login', { replace: true });
  }, [signOut, navigate]);

  const sidebarBody = (mobile: boolean) => {
    const isCollapsed = collapsed && !mobile;
    return (
      <div className="flex h-full flex-col gap-1 p-3">
        <div className="flex items-center justify-between gap-2">
          <Wordmark collapsed={isCollapsed} />
          {mobile && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close navigation"
              onClick={() => setMobileOpen(false)}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto pt-4" aria-label="Main">
          <NavSection
            items={PRIMARY_NAV}
            collapsed={isCollapsed}
            onNavigate={() => setMobileOpen(false)}
          />
          <NavSection
            items={WORKSPACE_NAV}
            title="Workspace"
            collapsed={isCollapsed}
            onNavigate={() => setMobileOpen(false)}
          />
          {user?.isSuperadmin && (
            <NavSection
              items={[{ to: '/admin', label: 'Admin', icon: Shield }]}
              title="System"
              collapsed={isCollapsed}
              onNavigate={() => setMobileOpen(false)}
            />
          )}
        </nav>

        {(aiProvider === 'mock' || simulatePublish) && !isCollapsed && (
          <div className="flex flex-wrap gap-1.5 px-1 pb-2">
            {aiProvider === 'mock' && (
              <Badge tone="warning" size="sm">
                Demo AI
              </Badge>
            )}
            {simulatePublish && (
              <Badge tone="info" size="sm">
                Simulated publishing
              </Badge>
            )}
          </div>
        )}

        {!mobile && (
          <button
            type="button"
            onClick={() => setCollapsed((current) => !current)}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'flex items-center gap-2.5 rounded-md border-t border-border py-2 text-xs text-subtle',
              'transition-colors hover:text-fg',
              isCollapsed ? 'justify-center px-0' : 'px-3',
            )}
          >
            {isCollapsed ? (
              <ChevronsRight className="size-4" aria-hidden />
            ) : (
              <>
                <ChevronsLeft className="size-4" aria-hidden />
                Collapse
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="flex min-h-dvh bg-bg">
      <aside
        className={cn(
          'hidden shrink-0 border-r border-border bg-sidebar transition-[width] duration-200 md:block',
          collapsed ? 'w-[4.5rem]' : 'w-60',
        )}
      >
        <div className="sticky top-0 h-dvh">{sidebarBody(false)}</div>
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 md:hidden"
          >
            <div
              className="absolute inset-0 bg-overlay backdrop-blur-[2px]"
              onClick={() => setMobileOpen(false)}
              aria-hidden
            />
            <motion.aside
              initial={{ x: -288 }}
              animate={{ x: 0 }}
              exit={{ x: -288 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="relative h-full w-72 border-r border-border bg-sidebar"
              aria-label="Navigation"
            >
              {sidebarBody(true)}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-surface/85 px-3 backdrop-blur md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="size-4" />
          </Button>

          <WorkspaceSwitcher />

          <div className="ml-auto flex items-center gap-1">
            <Button
              size="sm"
              className="hidden sm:inline-flex"
              leftIcon={<PenSquare className="size-4" />}
              onClick={() => navigate('/composer')}
            >
              New post
            </Button>
            <Button
              size="icon"
              className="sm:hidden"
              aria-label="New post"
              onClick={() => navigate('/composer')}
            >
              <PenSquare className="size-4" />
            </Button>

            <NotificationBell />
            <ThemeToggle />

            <DropdownMenu
              align="end"
              menuClassName="min-w-56"
              trigger={
                <button
                  type="button"
                  aria-label="Account menu"
                  className="ml-0.5 rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <Avatar
                    name={user?.name}
                    email={user?.email}
                    src={user?.avatarUrl}
                    size="sm"
                    className="ring-2 ring-transparent transition-[box-shadow] hover:ring-border-strong"
                  />
                </button>
              }
            >
              {(close) => (
                <>
                  <div className="px-2.5 py-2">
                    <p className="truncate text-sm font-medium text-fg">
                      {user?.name ?? 'Signed in'}
                    </p>
                    <p className="truncate text-xs text-subtle">{user?.email}</p>
                  </div>
                  <DropdownSeparator />
                  <DropdownItem
                    icon={<Settings />}
                    onSelect={() => {
                      close();
                      navigate('/settings');
                    }}
                  >
                    Settings
                  </DropdownItem>
                  <DropdownItem
                    icon={<Users />}
                    onSelect={() => {
                      close();
                      navigate('/team');
                    }}
                  >
                    Team & invites
                  </DropdownItem>
                  {user?.isSuperadmin && (
                    <DropdownItem
                      icon={<Shield />}
                      onSelect={() => {
                        close();
                        navigate('/admin');
                      }}
                    >
                      Admin
                    </DropdownItem>
                  )}
                  {devMode && (
                    <>
                      <DropdownSeparator />
                      <DropdownLabel>Dev mode</DropdownLabel>
                      <DropdownItem
                        icon={<Sparkles />}
                        onSelect={() => {
                          close();
                          navigate('/onboarding');
                        }}
                      >
                        Re-run onboarding
                      </DropdownItem>
                    </>
                  )}
                  <DropdownSeparator />
                  <DropdownItem
                    destructive
                    icon={<LogOut />}
                    onSelect={() => {
                      close();
                      void handleSignOut();
                    }}
                  >
                    Sign out
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-6 md:px-8 md:py-8">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto w-full max-w-6xl"
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}
